/** When a decision family is in the player's hands: "open" now, the round it opens in, or null when no later round opens it (a family held at the standing policy this round is "set by policy"). */
import type { ContentCatalog, TeamState } from "@bsim/contracts";
import type { DecisionFamily, DecisionId } from "@bsim/domain";
import { openDecisionIds } from "../results/openIds";
import { shortLabel } from "./decisionValues";

export function familyOpensIn(team: TeamState, catalog: ContentCatalog, family: DecisionFamily): "open" | number | null {
  const ids = catalog.decisions.filter((d) => d.family === family).map((d) => d.decisionId);
  const editable = team.draft?.editableDecisionIds;
  if (editable === undefined || ids.some((id) => editable.includes(id))) return "open";
  const now = team.round?.roundNo ?? 0;
  const later = team.cohort.complexityByRound.findIndex((level, round) => round > now && ids.some((id) => openDecisionIds(catalog, level, round).has(id)));
  return later === -1 ? null : later;
}

/** The round after this one in which a decision first opens to the player (a guided round's own `opensInRound`, else the catalog's round bands), null when no later round opens it. */
export function decisionOpensIn(team: TeamState, catalog: ContentCatalog, decisionId: string): number | null {
  const now = team.round?.roundNo ?? 0;
  const own = catalog.guided?.opensInRound[decisionId as DecisionId];
  if (own !== undefined && own > now) return own;
  const at = team.cohort.complexityByRound.findIndex((level, round) => round > now && (openDecisionIds(catalog, level, round) as ReadonlySet<string>).has(decisionId));
  return at === -1 ? null : at;
}

/** The early-open rule of a capital-need decision in words (", or now if the bank would fall ..."), empty for any other: the one place every "opens in round N" line takes it from, so no screen names a bare round for a decision that can open sooner. */
export const earlyOpenClause = (catalog: ContentCatalog, decisionId: string): string => {
  const need = catalog.guided?.unlockOnCapitalNeed;
  return need === undefined || !need.decisionIds.some((id) => id === decisionId) ? "" : `, or now if ${need.text}`;
};

/** "Opens in round 3" for a locked row's hover; "Set by your standing policy all game" when no round opens it. `early` is the decision's `earlyOpenClause`. */
export const opensInLine = (round: number | null, early = ""): string => (round === null ? "Set by your standing policy for the rest of the game" : `Opens in round ${round}${early}`);

/**
 * Why rows on a desk are locked this round, from the catalog's round bands: "Round 2: one decision on this desk is open; Securities make-up opens in round 3."
 * Null when nothing on the desk is locked (or the round has no band to read).
 */
export function deskLockLine(team: TeamState, catalog: ContentCatalog, family: DecisionFamily): string | null {
  const editable = team.draft?.editableDecisionIds;
  const round = team.round?.roundNo;
  if (editable === undefined || round === undefined) return null;
  const entries = catalog.decisions.filter((d) => d.family === family);
  const open = entries.filter((d) => editable.includes(d.decisionId));
  const locked = entries.filter((d) => !editable.includes(d.decisionId));
  if (locked.length === 0) return null;
  // A decision that can open sooner on a capital need is its own group, so its early-open rule rides with it alone.
  const by = new Map<string, { readonly round: number | null; readonly early: string; readonly names: string[] }>();
  for (const d of locked) {
    const round = decisionOpensIn(team, catalog, d.decisionId), early = earlyOpenClause(catalog, d.decisionId), key = `${round}|${early}`;
    by.set(key, { round, early, names: [...(by.get(key)?.names ?? []), shortLabel(d.label)] });
  }
  const when = [...by.values()].sort((a, b) => (a.round ?? Infinity) - (b.round ?? Infinity)).map(({ round: r, early, names }) => {
    const who = names.length > 3 ? `${names.length} more decisions` : names.join(", ");
    return r === null ? `${who} stay on your standing policy` : `${who} ${names.length === 1 ? "opens" : "open"} in round ${r}${early}`;
  });
  const now = open.length === 1 ? "one decision on this desk is open" : open.length === 0 ? "no decision on this desk is open" : `${open.length} decisions on this desk are open`;
  return `Round ${round}: ${now}; ${when.join("; ")}.`;
}
